/** Tipos y etiquetas de la ventana Agentes (una fila por run). Los datos los sirve /api/agents/{runs,summary,trace}. */

export const VIEWS = ["active", "issues", "history", "trace"] as const;
export type View = (typeof VIEWS)[number];
export const VIEW_LABEL: Record<View, string> = { active: "En curso", issues: "Incidencias", history: "Historial", trace: "Trazabilidad" };
export const VIEW_HELP: Record<View, string> = {
  active: "Runs que el proveedor o el orquestador dan por vivos ahora mismo. No incluye agentes que solo existen como contenedor.",
  issues: "Runs que terminaron con error o caducados. Cancelar un run no es una incidencia. Las misiones que esperan una decisión humana no aparecen aquí.",
  history: "Todos los runs terminados: finalizados, con error, cancelados y caducados.",
  trace: "Discrepancias de datos que se pueden consultar aunque no exista una fila de ejecución asociada.",
};

export const PERIODS = [["24h", "Últimas 24 h"], ["7d", "Últimos 7 días"], ["30d", "Últimos 30 días"], ["all", "Todo el historial"]] as const;
export type Period = (typeof PERIODS)[number][0];

export const PHASES = [["plan", "Planificación"], ["develop", "Desarrollo"], ["fix", "Corrección"], ["followup", "Instrucción"]] as const;
export const PHASE_LABEL: Record<string, string> = Object.fromEntries(PHASES);

/** Etiquetas de EJECUCIÓN: describen el run, no el agente que lo aloja. */
export const RUN_LABEL: Record<string, string> = { creating: "Iniciando", running: "Ejecución en curso", waiting: "Esperando", finished: "Finalizada", error: "Con error", cancelled: "Cancelada", expired: "Caducada" };
export const RUN_HELP: Record<string, string> = {
  creating: "El lanzamiento está registrado y el proveedor aún no ha confirmado el run.",
  running: "El proveedor informa que el run se está ejecutando.",
  waiting: "El proveedor informa el run en espera.",
  finished: "El run terminó correctamente.",
  error: "El run terminó con error.",
  cancelled: "El run se canceló antes de terminar.",
  expired: "El run caducó en el proveedor.",
};
export const RUN_TONE: Record<string, "good" | "warn" | "bad" | "info" | "neutral"> = { creating: "info", running: "info", waiting: "warn", finished: "good", error: "bad", cancelled: "neutral", expired: "bad" };
export const STATUS_BY_VIEW: Record<View, string[]> = { active: ["creating", "running", "waiting"], issues: ["error", "expired"], history: ["finished", "error", "cancelled", "expired"], trace: [] };

export const MISSION_STATUS_LABEL: Record<string, string> = {
  pending: "Pendiente", planning: "Planificando", awaiting_plan_approval: "Espera aprobación del plan", developing: "En desarrollo", awaiting_ci: "Esperando CI", in_review: "Espera aprobación de entrega",
  merging: "Esperando merge", done: "Terminada", blocked: "Bloqueada", error: "Error", awaiting_budget: "Esperando presupuesto", cancelled: "Cancelada",
};

/** Estado del agente persistente. Solo se muestra cuando aporta algo distinto de «activo». */
export const AGENT_STATUS_LABEL: Record<string, string> = { ACTIVE: "activo", LAUNCHING: "lanzando", ARCHIVED: "archivado", ERROR: "con error", DELETED: "eliminado" };
export const agentStatusText = (s: string) => AGENT_STATUS_LABEL[s] ?? s.toLowerCase();

export const USAGE_STATE_LABEL: Record<string, string> = { measured: "Medido", zero: "0 tokens medidos", pending: "Pendiente de sincronizar", unknown: "Desconocido (sin medición)" };
export const USAGE_STATE_HELP: Record<string, string> = {
  measured: "Consumo final medido por el proveedor.",
  zero: "El proveedor informó consumo final y es exactamente 0. No es lo mismo que desconocido.",
  pending: "Hay una medición parcial o el run sigue vivo y aún no hay ninguna. La cifra puede crecer.",
  unknown: "El run terminó y no hay ninguna medición registrada: no se puede afirmar que el consumo fuera 0.",
};

export const TRACE_KINDS = ["usage_without_run", "run_without_usage", "duplicate_usage", "terminal_without_provider_id", "agent_without_mission"] as const;
export type TraceKind = (typeof TRACE_KINDS)[number];
export const TRACE_LABEL: Record<TraceKind, string> = {
  usage_without_run: "Consumo sin run local", run_without_usage: "Runs terminados sin medición", duplicate_usage: "Runs con consumo duplicado",
  terminal_without_provider_id: "Runs terminados sin id de proveedor", agent_without_mission: "Agentes sin misión",
};
export const TRACE_HELP: Record<TraceKind, string> = {
  usage_without_run: "Registros de consumo por run que el proveedor informó y que no están enlazados a ningún run local. Se suman al consumo de la misión; no hay fila de ejecución que los explique.",
  run_without_usage: "Runs terminados con id de proveedor y sin ningún registro de consumo. El consumo es desconocido, no cero.",
  duplicate_usage: "Runs con más de un registro de consumo por run: el detalle muestra el último, pero los totales de la misión suman todos.",
  terminal_without_provider_id: "Runs que figuran como terminados y nunca recibieron confirmación del proveedor (sin id externo).",
  agent_without_mission: "Agentes cuya misión ya no existe. Solo atribuibles con «Todas las empresas».",
};

export interface RunRow {
  id: string; missionId: string; missionKey: string; missionTitle: string; missionStatus: string; projectId: string; projectName: string; repositoryId: string; repository: string;
  purpose: string; status: string; provider: string; startedAt: string | null; createdAt: string; finishedAt: string | null; durationMs: number | null; lastUpdateAt: string; pendingConfirmation: boolean;
  agent: { id: string; name: string; status: string; archived: boolean };
}
export interface RunsPage { rows: RunRow[]; total: number; page: number; pageSize: number; generatedAt: string }

export interface Summary {
  generatedAt: string;
  counts: { active: number; issues: number; history: number };
  capacity: { scope: "factory"; activeRuns: number; limit: number; waitingMissions: { scope: "selection"; count: number } };
  humanApproval: { scope: "selection"; missions: number };
  providers: string[];
  facets: { projects: { id: string; name: string }[]; repositories: { id: string; name: string; projectId: string }[] };
  traceability: { total: number; byKind: Record<TraceKind, number> };
}

export interface TraceItem {
  id: string; kind: TraceKind; at: string | null; missionId: string | null; missionKey: string | null; repository: string | null; provider: string | null; agentName: string | null;
  runId: string | null; purpose: string | null; status: string | null; tokens: number | null; completeness: string | null; source: string | null; externalId: string | null;
}
export interface TracePage { items: TraceItem[]; total: number; page: number; pageSize: number; counts: Record<TraceKind, number> }

export interface RunDetail {
  run: { id: string; purpose: string; mode: string; status: string; rawStatus: string | null; startedAt: string | null; finishedAt: string | null; createdAt: string; lastPolledAt: string | null; durationMs: number | null; pendingConfirmation: boolean; resultText: string | null; resultTruncated: boolean; branch: string | null; providerPrUrl: string | null };
  agent: { id: string; name: string; provider: string; status: string; archivedAt: string | null; url: string | null; createdAt: string; lastActivityAt: string | null; modelRequested: string | null };
  mission: { id: string; key: string; title: string; status: string; statusReason: string | null; blockCode: string | null };
  project: { id: string; name: string }; company: { id: string; name: string }; repository: { id: string; fullName: string };
  pullRequest: { number: number; url: string; state: string; draft: boolean } | null;
  sequence: { id: string; purpose: string; status: string; startedAt: string | null; finishedAt: string | null; durationMs: number | null; createdAt: string; current: boolean }[];
  usage: {
    state: "measured" | "zero" | "pending" | "unknown";
    tokens: { input: number; output: number; cacheRead: number; cacheWrite: number; total: number } | null;
    completeness: string | null; source: string | null; collectedAt: string | null; model: { requested: string | null; confirmed: string | null }; recordCount: number;
    mission: { totalTokens: number; pendingRecords: number; records: number };
  };
  technical: { runId: string; providerRunId: string | null; agentId: string; providerAgentId: string; idempotencyKey: string; provider: string; rawStatus: string | null; usageRecordIds: string[] };
}

/* ───────────── filtros en la URL (se conservan al abrir un detalle y volver) ───────────── */

export interface Filters { view: View; q: string; project: string; repo: string; phase: string; provider: string; status: string; period: Period; page: number; run: string; kind: TraceKind | "" }
export const DEFAULT_PERIOD: Period = "30d";
export const PAGE_SIZE = 25;

export function readFilters(sp: URLSearchParams): Filters {
  const view = sp.get("view") as View | null;
  const period = sp.get("period") as Period | null;
  const kind = sp.get("kind") as TraceKind | null;
  return {
    view: view && VIEWS.includes(view) ? view : "active", q: sp.get("q") ?? "", project: sp.get("project") ?? "", repo: sp.get("repo") ?? "", phase: sp.get("phase") ?? "", provider: sp.get("provider") ?? "",
    status: sp.get("status") ?? "", period: period && PERIODS.some((p) => p[0] === period) ? period : DEFAULT_PERIOD, page: Math.max(1, Number(sp.get("page")) || 1), run: sp.get("run") ?? "",
    kind: kind && TRACE_KINDS.includes(kind) ? kind : "",
  };
}

/** Solo viajan en la URL los valores que difieren del valor por defecto: enlaces cortos y estables. */
export function writeFilters(f: Filters): URLSearchParams {
  const p = new URLSearchParams();
  if (f.view !== "active") p.set("view", f.view);
  for (const [k, v] of [["q", f.q], ["project", f.project], ["repo", f.repo], ["phase", f.phase], ["provider", f.provider], ["status", f.status], ["run", f.run], ["kind", f.kind]] as const) if (v) p.set(k, v);
  if (f.period !== DEFAULT_PERIOD) p.set("period", f.period);
  if (f.page > 1) p.set("page", String(f.page));
  return p;
}

/** Parámetros que entiende el servidor (compartidos por la tabla y el resumen). */
export function serverParams(f: Filters, o: { withView: boolean }): URLSearchParams {
  const p = new URLSearchParams();
  if (f.q.trim()) p.set("q", f.q.trim());
  if (f.project) p.set("projectId", f.project);
  if (f.repo) p.set("repositoryId", f.repo);
  if (f.phase) p.set("phase", f.phase);
  if (f.provider) p.set("provider", f.provider);
  p.set("period", f.period);
  if (o.withView) { p.set("view", f.view); if (f.status) p.set("status", f.status); }
  return p;
}
